import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import SeriesCard from "../components/SeriesCard";
import SuggestForm from "../components/SuggestForm";
import TopEpisodesCarousel from "../components/TopEpisodesCarousel";
import { Sparkles, Tv2, Zap, Radio } from "lucide-react";

export default function Home() {
  const [series, setSeries] = useState([]);

  useEffect(() => {
    api.get("/series").then((r) => setSeries(r.data)).catch(() => {});
  }, []);

  const featured = series.filter((s) => !s.is_blocked).slice(0, 8);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-4 border-slate-900 bg-yellow-100">
        <div className="absolute inset-0 bg-halftone opacity-60 pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full border-4 border-[#ff2fa7]/40 opacity-70 animate-slow-spin" style={{ background: "repeating-conic-gradient(from 0deg, rgba(255,47,167,0.22) 0deg 12deg, rgba(0,229,255,0.16) 12deg 24deg)", filter: "blur(0.5px)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 bg-pink-500 text-white border-4 border-slate-900 rounded-full px-4 py-1 mb-6 shadow-brutal-sm">
              <Sparkles className="w-4 h-4" strokeWidth={3} />
              <span className="font-black uppercase text-xs tracking-widest">Retrăiește Copilăria</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.9] text-slate-900 text-bubble">
              DESENE<br />
              <span className="text-pink-500">CLASICE</span><br />
              ANII <span className="text-cyan-500">2000</span>
            </h1>
            <p className="mt-6 text-lg text-slate-700 font-medium max-w-md">
              Piratul Jack, Marcelino, Scooby Doo, Gemenii Cramp, Powerpuff,
              Dexter, Curaj... și toate desenele cu care ai crescut, într-un
              singur loc retro și colorat.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link
                to="/grila-tv"
                data-testid="hero-live-tv-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 text-white border-4 border-slate-900 rounded-full font-black uppercase tracking-wider shadow-brutal hover-press relative"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full bg-white animate-pulse"
                  style={{ boxShadow: "0 0 10px rgba(255,255,255,0.95)" }}
                />
                <Radio className="w-5 h-5" strokeWidth={3} /> Live TV
              </Link>
              <Link
                to="/seriale"
                data-testid="hero-browse-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 text-white border-4 border-slate-900 rounded-full font-black uppercase tracking-wider shadow-brutal hover-press"
              >
                <Tv2 className="w-5 h-5" strokeWidth={3} /> Vezi Toate Serialele
              </Link>
              <Link
                to="/inregistrare"
                data-testid="hero-signup-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-lime-400 text-slate-900 border-4 border-black rounded-full font-black uppercase tracking-wider shadow-brutal hover-press"
              >
                <Zap className="w-5 h-5" strokeWidth={3} /> Cont Gratuit
              </Link>
            </div>
          </div>

          {/* Decorative CRT TV */}
          <div className="relative">
            <div className="relative mx-auto max-w-md">
              <div className="rounded-3xl border-4 border-slate-900 bg-slate-800 p-4 shadow-brutal-lg">
                <div className="rounded-2xl overflow-hidden border-4 border-slate-900 relative aspect-video bg-slate-900">
                  <img
                    src="https://customer-assets.emergentagent.com/job_cartoon-kids-9/artifacts/l6drdwu4_1a7cbdf8-3d14-4348-b3c4-c91e0f1620bd.png"
                    alt="Desene anii 2000"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: "brightness(1.25) saturate(1.15)" }}
                  />
                </div>
                <div className="flex justify-between items-center mt-4">
                  <div className="flex gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-slate-900" />
                    <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-slate-900" />
                  </div>
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-yellow-300 border-2 border-slate-900" />
                    <div className="w-6 h-6 rounded-full bg-cyan-300 border-2 border-slate-900" />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-3 left-12 right-12 h-3 bg-slate-900 rounded-b-2xl" />
              <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex gap-16">
                <div className="w-1.5 h-12 bg-slate-900 rotate-[-25deg] origin-bottom" />
                <div className="w-1.5 h-12 bg-slate-900 rotate-[25deg] origin-bottom" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 text-bubble-sm">
            Desene <span className="text-pink-500">Recomandate</span>
          </h2>
          <Link
            to="/seriale"
            className="px-4 py-2 bg-cyan-400 border-4 border-slate-900 rounded-full font-black uppercase text-sm hover-press"
            data-testid="see-all-link"
          >
            Vezi Toate →
          </Link>
        </div>

        {featured.length === 0 ? (
          <div className="text-center py-16 font-pixel text-3xl text-slate-400">SE ÎNCARCĂ...</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {featured.map((s, i) => (
              <SeriesCard key={s.id} series={s} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* TOP EPISODES CAROUSEL — most-viewed, one per series */}
      <TopEpisodesCarousel />

      {/* Decade badges */}
      <section className="relative border-t-4 border-b-4 border-[#ff2fa7]/60 bg-[#0a0f0d] overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 20% 50%, rgba(255,47,167,0.10), transparent 55%), radial-gradient(ellipse at 80% 50%, rgba(0,229,255,0.10), transparent 55%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-3 gap-6">
          {[
            { y: "1990-2002", title: "Epoca de Aur", color: "bg-pink-500" },
            { y: "2003-2006", title: "Epoca de Mijloc", color: "bg-yellow-300" },
            { y: "2007-2012", title: "Apusul Epocii", color: "bg-lime-400" },
          ].map((b) => (
            <div key={b.y} className={`${b.color} border-4 border-slate-900 rounded-2xl p-6 shadow-brutal text-slate-900`}>
              <div className="font-pixel text-2xl">{b.y}</div>
              <div className="font-display font-black text-3xl mt-1">{b.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Suggest a cartoon */}
      <SuggestForm />
    </div>
  );
}
